import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, AlertTriangle, Loader2 } from 'lucide-react';
import { db } from '../../../firebaseConfig';

// IMPORTANT: Increment this when building a new version
const CURRENT_APP_VERSION = "1.0.0";

// Helper to compare versions (e.g. 1.0.1 > 1.0.0)
const isNewVersionAvailable = (current, latest) => {
  if (!latest) return false;
  const currParts = String(current).split('.').map(Number);
  const latestParts = String(latest).split('.').map(Number);
  for (let i = 0; i < Math.max(currParts.length, latestParts.length); i++) {
    const curr = currParts[i] || 0;
    const lat = latestParts[i] || 0;
    if (lat > curr) return true;
    if (lat < curr) return false;
  }
  return false;
};

const AppUpdater = ({ children }) => {
  const [updateData, setUpdateData] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'settings', 'app_config'), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.currentVersion && isNewVersionAvailable(CURRENT_APP_VERSION, data.currentVersion) && data.forceUpdate) {
          setUpdateData(data);
          setShowModal(true);
        } else {
          setShowModal(false);
        }
      }
    }, (error) => {
      console.error("Error fetching app config:", error);
    });
    return () => unsubscribe();
  }, []);

  const handleUpdate = async () => {
    if (updateData?.updateUrl) {
      setIsUpdating(true);
      window.location.href = updateData.updateUrl;
    }
  };

  if (showModal) {
    return (
      <AnimatePresence>
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{
            position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            zIndex: 99999,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            padding: '20px',
            fontFamily: "'Inter', sans-serif"
          }}
        >
          <motion.div 
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            style={{
              background: 'linear-gradient(145deg, #1e1e1e, #141414)',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.5), inset 0 1px 1px rgba(255,255,255,0.1)',
              borderRadius: '24px',
              padding: '40px 30px',
              width: '100%',
              maxWidth: '400px',
              textAlign: 'center',
              color: '#fff'
            }}
          >
            <div style={{
              width: '70px', height: '70px',
              background: 'rgba(255, 215, 0, 0.1)',
              borderRadius: '50%',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              margin: '0 auto 20px auto',
              border: '2px solid rgba(255, 215, 0, 0.3)'
            }}>
              <AlertTriangle color="#FFD700" size={34} />
            </div>
            
            <h2 style={{ margin: '0 0 10px 0', fontSize: '1.5rem', fontWeight: 'bold' }}>Update Required</h2>
            <p style={{ color: '#aaa', fontSize: '0.95rem', lineHeight: '1.5', margin: '0 0 25px 0' }}>
              A new version ({updateData?.currentVersion}) of the Classic Tow app is available. You must update to continue using the app.
            </p>

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleUpdate}
              disabled={isUpdating}
              style={{
                width: '100%',
                padding: '16px',
                background: isUpdating ? 'linear-gradient(135deg, #888, #555)' : 'linear-gradient(135deg, #FFD700, #F39C12)',
                color: isUpdating ? '#fff' : '#000',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: isUpdating ? 'not-allowed' : 'pointer',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '10px',
                boxShadow: isUpdating ? 'none' : '0 4px 15px rgba(255, 215, 0, 0.3)'
              }}
            >
              {isUpdating ? (
                <>
                  <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: 'linear' }} style={{ display: 'flex' }}>
                    <Loader2 size={20} />
                  </motion.div>
                  Downloading Update...
                </>
              ) : (
                <>
                  <Download size={20} />
                  Download Update
                </>
              )}
            </motion.button>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  }

  return <>{children}</>;
};

export default AppUpdater;
